import { apiClient } from "@/lib/api-client";
import type { Company, CompanyFilters, CreateCompanyData, CreateCompanyWithTeamData } from "@/types/company";

/**
 * Get list of companies with optional filters
 */
export async function getCompanies(filters?: CompanyFilters): Promise<Company[]> {
    const params: Record<string, string | number | boolean> = {};

    if (filters?.search) {
        params.search = filters.search;
    }
    if (filters?.company_type) {
        params.company_type = filters.company_type;
    }
    if (filters?.is_active !== undefined) {
        params.is_active = filters.is_active;
    }
    if (filters?.country_code) {
        params.country_code = filters.country_code;
    }
    if (filters?.skip !== undefined) {
        params.skip = filters.skip;
    }
    if (filters?.limit !== undefined) {
        params.limit = filters.limit;
    }

    return apiClient<Company[]>("/companies", {
        method: "GET",
        params,
    });
}

/**
 * Get a single company by ID
 */
export async function getCompany(companyId: string): Promise<Company> {
    return apiClient<Company>(`/companies/${companyId}`, {
        method: "GET",
    });
}

/**
 * Create a new company
 */
export async function createCompany(data: CreateCompanyData): Promise<Company> {
    return apiClient<Company>("/companies", {
        method: "POST",
        body: JSON.stringify(data),
    });
}

/**
 * Create a new company with team members
 */
export async function createCompanyWithTeam(data: CreateCompanyWithTeamData): Promise<Company> {
    return apiClient<Company>("/companies/with-team", {
        method: "POST",
        body: JSON.stringify(data),
    });
}

/**
 * Update an existing company
 */
export async function updateCompany(companyId: string, data: Partial<CreateCompanyData>): Promise<Company> {
    return apiClient<Company>(`/companies/${companyId}`, {
        method: "PUT",
        body: JSON.stringify(data),
    });
}

/**
 * Delete a company (soft delete)
 */
export async function deleteCompany(companyId: string): Promise<{ message: string; company_id: string; deleted_at: string }> {
    return apiClient<{ message: string; company_id: string; deleted_at: string }>(`/companies/${companyId}`, {
        method: "DELETE",
    });
}

/**
 * Verify invitation token
 */
export async function verifyInvitationToken(token: string): Promise<{
    email: string;
    company_id: string;
    company_name: string;
    company_type: string | null;
    valid: boolean;
    user_exists: boolean;
}> {
    return apiClient<{
        email: string;
        company_id: string;
        company_name: string;
        company_type: string | null;
        valid: boolean;
        user_exists: boolean;
    }>(`/auth/verify-invitation/${token}`, {
        method: "GET",
        requireAuth: false,
    });
}

/**
 * Accept invitation for existing user (no password required)
 */
export async function acceptInvitation(token: string): Promise<{
    message: string;
    company_id: string;
    company_name: string;
    company_type: string | null;
}> {
    return apiClient<{
        message: string;
        company_id: string;
        company_name: string;
        company_type: string | null;
    }>("/auth/accept-invitation", {
        method: "POST",
        body: JSON.stringify({ token }),
        requireAuth: false,
    });
}

/**
 * Setup password for invited user
 */
export async function setupPassword(token: string, password: string, passwordConfirm: string): Promise<{ message: string }> {
    return apiClient<{ message: string }>("/auth/setup-password", {
        method: "POST",
        body: JSON.stringify({
            token,
            password,
            password_confirm: passwordConfirm,
        }),
        requireAuth: false,
    });
}

/**
 * Get all companies associated with the current user
 */
export async function getUserCompanies(): Promise<
    Array<{
        company_id: string;
        company_name: string;
        company_type: string | null;
        role: string;
        is_primary_company: boolean;
    }>
> {
    return apiClient<
        Array<{
            company_id: string;
            company_name: string;
            company_type: string | null;
            role: string;
            is_primary_company: boolean;
        }>
    >("/auth/me/companies", {
        method: "GET",
        requireAuth: true,
    });
}

/**
 * Invite a team member to a company
 */
export async function inviteTeamMember(
    companyId: string,
    data: {
        email: string;
        first_name?: string;
        last_name?: string;
        role: "operator" | "company_admin";
    },
): Promise<{
    message: string;
    invitation_id?: string;
    user_id?: string;
    email: string;
}> {
    return apiClient<{
        message: string;
        invitation_id?: string;
        user_id?: string;
        email: string;
    }>(`/companies/${companyId}/invitations`, {
        method: "POST",
        body: JSON.stringify({
            email: data.email,
            first_name: data.first_name,
            last_name: data.last_name,
            role: data.role,
        }),
        requireAuth: true,
    });
}

/**
 * Create and activate a team member directly (no invitation email)
 */
export async function createTeamMemberDirect(
    companyId: string,
    data: {
        email: string;
        first_name?: string;
        last_name?: string;
        role: "operator" | "company_admin";
        password: string;
        password_confirm: string;
    },
): Promise<{
    message: string;
    user_id: string;
    email: string;
    activation_mode: "direct";
}> {
    return apiClient<{
        message: string;
        user_id: string;
        email: string;
        activation_mode: "direct";
    }>(`/companies/${companyId}/team/direct-create`, {
        method: "POST",
        body: JSON.stringify(data),
        requireAuth: true,
    });
}

/**
 * Update a team member's information in a company
 */
export async function updateTeamMember(
    companyId: string,
    userId: string,
    data: {
        role?: "operator" | "company_admin";
        is_active?: boolean;
    },
): Promise<{
    message: string;
    user_id: string;
    company_id: string;
}> {
    return apiClient<{
        message: string;
        user_id: string;
        company_id: string;
    }>(`/companies/${companyId}/team/${userId}`, {
        method: "PUT",
        body: JSON.stringify(data),
        requireAuth: true,
    });
}

/**
 * Remove a team member from a company
 */
export async function removeTeamMember(
    companyId: string,
    userId: string,
    options?: {
        send_notification_email?: boolean;
    },
): Promise<{
    message: string;
    user_id: string;
    company_id: string;
}> {
    return apiClient<{
        message: string;
        user_id: string;
        company_id: string;
    }>(`/companies/${companyId}/team/${userId}`, {
        method: "DELETE",
        body: JSON.stringify({
            send_notification_email: options?.send_notification_email || false,
        }),
        requireAuth: true,
    });
}

/**
 * Resend an invitation email for a pending invitation
 */
export async function resendInvitation(
    companyId: string,
    invitationId: string,
): Promise<{
    message: string;
    invitation_id: string;
    email: string;
}> {
    return apiClient<{
        message: string;
        invitation_id: string;
        email: string;
    }>(`/companies/${companyId}/invitations/${invitationId}/resend`, {
        method: "POST",
        requireAuth: true,
    });
}

/**
 * Get pending invitations for a company
 */
export async function getCompanyInvitations(companyId: string): Promise<
    Array<{
        invitation_id: string;
        email: string;
        role: string;
        invited_by_user_id: string;
        expires_at: string;
        created_at: string;
    }>
> {
    return apiClient<
        Array<{
            invitation_id: string;
            email: string;
            role: string;
            invited_by_user_id: string;
            expires_at: string;
            created_at: string;
        }>
    >(`/companies/${companyId}/invitations`, {
        method: "GET",
    });
}

/**
 * Get all team members for a company
 */
/**
 * Validate a company (mark as verified)
 */
export async function validateCompany(companyId: string): Promise<{ message: string; company_id: string; is_verified: boolean; verified_at: string | null }> {
    return apiClient<{ message: string; company_id: string; is_verified: boolean; verified_at: string | null }>(`/companies/${companyId}/validate`, {
        method: "POST",
    });
}

/**
 * Remove validation from a company
 */
export async function unvalidateCompany(companyId: string): Promise<{ message: string; company_id: string; is_verified: boolean }> {
    return apiClient<{ message: string; company_id: string; is_verified: boolean }>(`/companies/${companyId}/unvalidate`, {
        method: "POST",
    });
}

export async function getCompanyTeam(companyId: string): Promise<
    Array<{
        user_id: string;
        email: string;
        first_name: string | null;
        last_name: string | null;
        full_name: string;
        role: string;
        is_active: boolean;
        avatar_url: string | null;
        job_title: string | null;
        department: string | null;
        last_login_at: string | null;
        created_at: string;
        is_primary_company: boolean;
        can_edit: boolean;
        can_delete: boolean;
    }>
> {
    return apiClient<
        Array<{
            user_id: string;
            email: string;
            first_name: string | null;
            last_name: string | null;
            full_name: string;
            role: string;
            is_active: boolean;
            avatar_url: string | null;
            job_title: string | null;
            department: string | null;
            last_login_at: string | null;
            created_at: string;
            is_primary_company: boolean;
            can_edit: boolean;
            can_delete: boolean;
        }>
    >(`/companies/${companyId}/team`, {
        method: "GET",
    });
}
